import apiClient from '../client';
import { ApiUrls } from '../ApiUrls';
import type { Project, ProjectFormData, ProjectMetaData, SubDomainData } from '../../types/project.types';
import type { BulkUpdateProjectItem, BulkUpdateProjectsResponse } from '../../types/subscription.types';
import { readAttachmentName, readAttachmentStatus } from '../../utils/attachment-readiness';

/** A scope's uploaded file, normalized from the raw backend row. */
export interface ScopeAttachmentDto {
  id: string;
  name: string;
  fileSize?: number;
  /** Indexing status: PENDING | PROCESSING | READY (or null for legacy rows). */
  processingStatus: string | null;
}

export interface StartupDto {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  userId?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Adapter: project rows now back the "Startup" concept end-to-end. This shapes
 * a Project record into the StartupDto consumers still pass around. Once every
 * caller switches to a Project type directly, this can be deleted.
 */
function projectToStartupDto(p: any): StartupDto {
  return {
    id: p?.id,
    name: p?.name,
    slug: p?.slug,
    description: p?.description ?? null,
    userId: p?.userId ?? null,
    createdAt: p?.createdAt,
    updatedAt: p?.updatedAt,
  };
}

/**
 * Adapter: the new scopes table column is `title`, but the rest of the app
 * keeps using `name` (form fields, list rendering, etc.). Translate at the
 * boundary so consumers don't churn.
 */
function scopeRowToDto(s: any): ScopeDto {
  return {
    id: s?.id,
    startupId: s?.projectId ?? null,
    name: s?.title ?? s?.name ?? '',
    description: s?.description ?? null,
    userId: s?.userId ?? null,
    industry: null,
    status: s?.status ?? null,
    iceScore: s?.iceScore ?? null,
    createdAt: s?.createdAt,
    updatedAt: s?.updatedAt,
  };
}

export interface ScopeDto {
  id: string;
  startupId?: string | null;
  name: string;
  description?: string | null;
  userId?: string | null;
  industry?: string | null;
  /** Backend scope status (free string; the UI maps known values to its badge set). */
  status?: string | null;
  /** Composite ICE score returned by the backend, or null when never evaluated. */
  iceScore?: number | null;
  createdAt: string;
  updatedAt: string;
}

/** Server-side sort fields supported by `GET /projects/:id/scopes`. */
export type ScopeSortBy = 'createdAt' | 'updatedAt' | 'iceScore';
export type ScopeSortOrder = 'ASC' | 'DESC';

/** Query params for the paginated scopes endpoint. */
export interface ScopesQuery {
  page?: number;
  limit?: number;
  /** Case-insensitive title search. */
  search?: string;
  sortBy?: ScopeSortBy;
  sortOrder?: ScopeSortOrder;
}

export interface ScopePaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface PaginatedScopes {
  scopes: ScopeDto[];
  pagination: ScopePaginationMeta;
}

export interface ConversationSummaryDto {
  id: string;
  title: string;
  mode: 'clarify' | 'prototyper' | 'strategy';
  createdAt: string;
  updatedAt: string;
}

/** Startup-level Discussion Room (one per startup). */
export interface DiscussionRoomDto {
  id: string;
  projectId: string;
  userId?: string | null;
  title: string;
  createdAt: string;
  updatedAt: string;
}

class ProjectService {
    /**
     * Create a new project
     */
    async createProject(projectData: { name: string; description?: string }): Promise<Project> {
        try {
            const response = await apiClient.post<Project>(ApiUrls.createProject, projectData);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Create a sample project
     */
    async createSampleProject(): Promise<Project> {
        try {
            const response = await apiClient.post<Project>(ApiUrls.createSampleProject);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get a project by ID
     */
    async getProject(projectId: string): Promise<Project> {
        try {
            const response = await apiClient.get<Project>(ApiUrls.getProject(projectId));
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get user projects with pagination
     */
    async getUserProjects(limit: number = 50, page: number = 1): Promise<Project[]> {
        try {
            const response = await apiClient.get<Project[]>(
                ApiUrls.getUserProjectsWithPagination(limit, page)
            );
            return response.data || [];
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Update a project
     */
    async updateProject(projectId: string, projectData: ProjectFormData): Promise<Project> {
        try {
            const response = await apiClient.patch<any>(
                ApiUrls.updateProject(projectId),
                projectData
            );
            return response.data?.data || response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Delete a project
     */
    async deleteProject(projectId: string): Promise<void> {
        try {
            await apiClient.delete(ApiUrls.deleteProject(projectId));
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get project metadata (AI-generated project details)
     */
    async getProjectMetadata(params: { user_message: string }): Promise<ProjectMetaData> {
        try {
            const response = await apiClient.post<ProjectMetaData>(
                ApiUrls.projectMetadata(),
                params
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Check site availability
     */
    async checkSiteAvailability(data: SubDomainData): Promise<any> {
        try {
            const response = await apiClient.post(ApiUrls.checkSiteAvailability, data);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    // ─── Startup methods ────────────────────────────────────────────────────────

    async getUserStartups(): Promise<StartupDto[]> {
        // Now sourced from /projects — every project is a startup in the new model.
        try {
            const response = await apiClient.get<any>(ApiUrls.getUserProjectsWithPagination(100, 1));
            const list = Array.isArray(response.data)
                ? response.data
                : (response.data?.data ?? []);
            return list.map(projectToStartupDto);
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    async getStartupById(startupId: string): Promise<StartupDto> {
        // Now sourced from /projects/:id.
        try {
            const response = await apiClient.get<any>(ApiUrls.getProject(startupId));
            const body = response.data?.data ?? response.data;
            return projectToStartupDto(body);
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    async createStartup(data: {
        name: string;
        description?: string;
        project_type?: string;
        language?: string;
        industry?: string;
        target_icp?: string;
        problem_details?: string;
    }): Promise<StartupDto> {
        // Step 2 of the startup→project consolidation: hit the existing /projects
        // endpoint and map its CreateProjectResponseDto ({ project_id, name, slug })
        // back into the StartupDto shape callers expect.
        try {
            const response = await apiClient.post<any>(ApiUrls.createProject, data);
            const body = response.data?.data ?? response.data;
            const now = new Date().toISOString();
            return {
                id: body.project_id ?? body.id,
                name: body.name ?? data.name,
                slug: body.slug,
                description: data.description ?? null,
                userId: null,
                createdAt: now,
                updatedAt: now,
            } as StartupDto;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    async updateStartup(
        startupId: string,
        data: {
            name?: string;
            description?: string;
            project_type?: string;
            language?: string;
            industry?: string;
            target_icp?: string;
            problem_details?: string;
        },
    ): Promise<StartupDto> {
        // Now hits /projects/:id (PATCH) — startup === project.
        try {
            const response = await apiClient.patch<any>(ApiUrls.updateProject(startupId), data);
            const body = response.data?.data ?? response.data;
            return projectToStartupDto(body);
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    async deleteStartup(startupId: string): Promise<void> {
        // Now hits /projects/:id (DELETE) — startup === project.
        try {
            await apiClient.delete(ApiUrls.deleteProject(startupId));
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    // ─── Scope methods ──────────────────────────────────────────────────────────

    async getStartupScopes(startupId: string): Promise<ScopeDto[]> {
        // Sourced from /projects/:id/scopes. Backend returns rows from the new
        // scopes table whose column is `title`; the rest of the app uses `name`.
        // Translate here so the existing UI stays unchanged.
        try {
            const response = await apiClient.get<any>(ApiUrls.getStartupScopes(startupId));
            const list = Array.isArray(response.data) ? response.data : (response.data?.data ?? []);
            return list.map(scopeRowToDto);
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Paginated + searchable + sortable scopes list (server-driven), consuming
     * `GET /projects/:id/scopes?page&limit&search&sortBy&sortOrder`. Tolerant of
     * the legacy bare-array response (older backends) — it synthesises a single
     * page of pagination meta so callers can render uniformly.
     */
    async listScopes(startupId: string, query: ScopesQuery = {}): Promise<PaginatedScopes> {
        try {
            const params: Record<string, string | number> = {};
            if (query.page != null) params.page = query.page;
            if (query.limit != null) params.limit = query.limit;
            const search = query.search?.trim();
            if (search) params.search = search;
            if (query.sortBy) params.sortBy = query.sortBy;
            if (query.sortOrder) params.sortOrder = query.sortOrder;

            const response = await apiClient.get<any>(ApiUrls.getStartupScopes(startupId), {
                params,
            });
            const body = response.data;
            const rows = Array.isArray(body) ? body : (body?.data ?? []);
            const scopes = rows.map(scopeRowToDto);

            const meta = body?.pagination;
            const limit = meta?.limit ?? query.limit ?? (scopes.length || 1);
            const total = meta?.total ?? scopes.length;
            const page = meta?.page ?? query.page ?? 1;
            const totalPages = meta?.totalPages ?? (limit ? Math.max(1, Math.ceil(total / limit)) : 1);

            return {
                scopes,
                pagination: {
                    page,
                    limit,
                    total,
                    totalPages,
                    hasNext: meta?.hasNext ?? page < totalPages,
                    hasPrevious: meta?.hasPrevious ?? page > 1,
                },
            };
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    async getScopeById(startupId: string, scopeId: string): Promise<ScopeDto> {
        try {
            const response = await apiClient.get<any>(ApiUrls.getScopeById(startupId, scopeId));
            const body = response.data?.data ?? response.data;
            return scopeRowToDto(body);
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    async createScope(
        startupId: string,
        data: { name: string; description?: string },
    ): Promise<ScopeDto> {
        // Backend expects `title`; frontend uses `name`. Translate at the boundary.
        try {
            const response = await apiClient.post<any>(ApiUrls.createScope(startupId), {
                title: data.name,
                ...(data.description !== undefined && { description: data.description }),
            });
            const body = response.data?.data ?? response.data;
            return scopeRowToDto(body);
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    async updateScope(
        startupId: string,
        scopeId: string,
        data: { name?: string; description?: string },
    ): Promise<ScopeDto> {
        try {
            const response = await apiClient.patch<any>(ApiUrls.updateScope(startupId, scopeId), {
                ...(data.name !== undefined && { title: data.name }),
                ...(data.description !== undefined && { description: data.description }),
            });
            const body = response.data?.data ?? response.data;
            return scopeRowToDto(body);
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    async deleteScope(startupId: string, scopeId: string): Promise<void> {
        try {
            await apiClient.delete(ApiUrls.deleteScope(startupId, scopeId));
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /** Delete a conversation (AI API: `DELETE /projects/:id/conversation/:conversationId`). */
    async deleteConversation(projectId: string, conversationId: string): Promise<void> {
        try {
            await apiClient.delete(ApiUrls.deleteConversation(projectId, conversationId));
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    async listScopeConversations(
        startupId: string,
        scopeId: string,
        mode?: 'clarify' | 'prototyper' | 'strategy',
    ): Promise<ConversationSummaryDto[]> {
        try {
            const response = await apiClient.get<ConversationSummaryDto[]>(
                ApiUrls.listScopeConversations(startupId, scopeId, mode),
            );
            const list = Array.isArray(response.data) ? response.data : [];
            // "Recent chats" is ordered most-recently-updated first, so a chat
            // bumps to the top whenever it gets a new turn. Fall back to
            // createdAt when updatedAt is missing.
            return [...list].sort(
                (a, b) =>
                    new Date(b.updatedAt ?? b.createdAt).getTime() -
                    new Date(a.updatedAt ?? a.createdAt).getTime(),
            );
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    async createScopeConversation(
        startupId: string,
        scopeId: string,
        mode: 'clarify' | 'prototyper' | 'strategy',
    ): Promise<ConversationSummaryDto> {
        try {
            const response = await apiClient.post<ConversationSummaryDto>(
                ApiUrls.createScopeConversation(startupId, scopeId),
                { mode },
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    async updateScopeConversationMode(
        startupId: string,
        scopeId: string,
        conversationId: string,
        mode: 'clarify' | 'prototyper' | 'strategy',
    ): Promise<ConversationSummaryDto> {
        try {
            const response = await apiClient.patch<ConversationSummaryDto>(
                ApiUrls.updateScopeConversation(startupId, scopeId, conversationId),
                { mode },
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    // ─── Discussion Room ──────────────────────────────────────────────────────

    /** Get-or-create the startup's single Discussion Room (Nest returns a list). */
    async getDiscussionRoom(startupId: string): Promise<DiscussionRoomDto> {
        try {
            const response = await apiClient.get<DiscussionRoomDto[]>(
                ApiUrls.discussionRooms(startupId),
            );
            const rooms = Array.isArray(response.data) ? response.data : [];
            if (!rooms.length) {
                throw new Error('Discussion room was not created');
            }
            return rooms[0];
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /** A room's conversations, most-recently-updated first (like recent chats). */
    async listDiscussionConversations(
        startupId: string,
        roomId: string,
    ): Promise<ConversationSummaryDto[]> {
        try {
            const response = await apiClient.get<ConversationSummaryDto[]>(
                ApiUrls.listDiscussionConversations(startupId, roomId),
            );
            const list = Array.isArray(response.data) ? response.data : [];
            return [...list].sort(
                (a, b) =>
                    new Date(b.updatedAt ?? b.createdAt).getTime() -
                    new Date(a.updatedAt ?? a.createdAt).getTime(),
            );
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /** Create a new (Nest-owned) conversation shell inside a Discussion Room. */
    async createDiscussionConversation(
        startupId: string,
        roomId: string,
        title?: string,
    ): Promise<ConversationSummaryDto> {
        try {
            const response = await apiClient.post<ConversationSummaryDto>(
                ApiUrls.createDiscussionConversation(startupId, roomId),
                title ? { title } : {},
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get project conversations
     */
    async getProjectConversations(projectId: string): Promise<any> {
        try {
            const response = await apiClient.get(ApiUrls.projectConversations(projectId));
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Upload project files/attachments
     */
    async uploadProjectFiles(
        projectId: string,
        formData: FormData,
        options?: { extractTone?: boolean },
    ): Promise<any> {
        try {
            if (options?.extractTone) {
                formData.set('extract_tone', 'true');
            }
            // Ensure we don't send the default application/json Content-Type from the client instance.
            // Passing an explicit undefined Content-Type tells the browser to set the multipart boundary.
            const response = await apiClient.post(ApiUrls.uploadProjectFiles(projectId), formData, {
                headers: { 'Content-Type': undefined as any },
            });
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get project attachments
     */
    async getProjectAttachments(projectId: string): Promise<any> {
        try {
            const response = await apiClient.get(ApiUrls.getProjectAttachments(projectId));
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get design system for a project
     */
    async getDesignSystem(projectId: string): Promise<any> {
        try {
            const url = `${ApiUrls.getProject(projectId)}/design-system`;
            const response = await apiClient.get(url);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Update (patch) design system for a project
     * Expects a plain design system object (no wrapper)
     */
    async updateDesignSystem(projectId: string, payload: any): Promise<any> {
        try {
            const url = `${ApiUrls.getProject(projectId)}/design-system`;
            const response = await apiClient.patch(url, payload);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Upload a logo specifically for the design system
     * Expects multipart/form-data with field name 'logo'
     */
    async uploadDesignSystemLogo(projectId: string, formData: FormData): Promise<any> {
        try {
            const url = `${ApiUrls.getProject(projectId)}/design-system/logo`;
            // Ensure the default application/json header is not sent so the browser can set the multipart boundary.
            const response = await apiClient.post(url, formData, {
                headers: { 'Content-Type': undefined as any },
            });
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Delete the design-system logo for a project
     * Endpoint: DELETE /projects/{projectId}/design-system/logo
     */
    async deleteDesignSystemLogo(projectId: string): Promise<void> {
        try {
            const url = `${ApiUrls.getProject(projectId)}/design-system/logo`;
            await apiClient.delete(url);
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Extract a partial design system from 1..N screenshots. Every returned field
     * is nullable — the model returns null for anything it can't confidently detect.
     * Endpoint: POST {aiApi}/design-system/extract (direct FE → Flask, same
     * pattern as SSE conversations — user JWT decoded by Flask's token_required).
     */
    async extractDesignSystem(images: File[]): Promise<any> {
        try {
            const formData = new FormData();
            for (const file of images) {
                formData.append('images', file);
            }
            // 3-minute timeout — Gemini 2.5 Pro vision extraction on 1–6
            // screenshots typically takes 20–60s and can spike higher when
            // upstream tracing (LangSmith) is slow. The default 30s in
            // apiClient (`client.ts:9`) was cancelling in-flight extractions.
            const response = await apiClient.post(ApiUrls.extractDesignSystem, formData, {
                headers: { 'Content-Type': undefined as any },
                timeout: 180000,
            });
            // Flask returns `{ data: ExtractedDesignSystem }`. Nest used to
            // unwrap this before proxying; now we're direct, so unwrap here.
            return response.data?.data ?? response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Delete project attachment
     */
    async deleteProjectAttachment(fileId: string): Promise<void> {
        try {
            await apiClient.delete(ApiUrls.deleteProjectAttachment(fileId));
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Indexing readiness for a set of project attachments. The upload endpoint
     * returns as soon as the row is written — the file is embedded afterwards
     * on a Celery worker — so callers that search attachment vectors must wait
     * for `all_ready` first. A `null` status is a pre-migration row and counts
     * as ready (the API folds that into `all_ready` for us).
     */
    async getAttachmentsReadiness(
        attachmentIds: string[],
    ): Promise<{ all_ready: boolean; statuses: Record<string, string | null> }> {
        try {
            const response = await apiClient.get<any>(ApiUrls.attachmentsReady(attachmentIds));
            const body = response.data?.data ?? response.data;
            return {
                all_ready: Boolean(body?.all_ready),
                statuses: body?.statuses ?? {},
            };
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    // ─── Scope attachments ─────────────────────────────────────────────────────

    /**
     * Upload a single file to a scope (multipart). Hits the dedicated
     * /projects/:p/scopes/:s/attachments endpoint that writes to the
     * scope_attachments table (not the project attachments table).
     */
    async uploadScopeAttachment(
        projectId: string,
        scopeId: string,
        formData: FormData,
    ): Promise<any> {
        try {
            const response = await apiClient.post(
                ApiUrls.uploadScopeAttachment(projectId, scopeId),
                formData,
                { headers: { 'Content-Type': undefined as any } },
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    async getScopeAttachments(projectId: string, scopeId: string): Promise<ScopeAttachmentDto[]> {
        try {
            const response = await apiClient.get<any>(
                ApiUrls.getScopeAttachments(projectId, scopeId),
            );
            const list = Array.isArray(response.data)
                ? response.data
                : (response.data?.data ?? []);
            // Normalize the raw row shape once, here — callers get a typed DTO
            // instead of re-deriving snake/camel field names each time.
            return (list as any[]).map((row) => ({
                id: String(row?.id ?? row?.attachment_id ?? row?.attachmentId ?? ''),
                name: readAttachmentName(row),
                fileSize: row?.fileSize ?? row?.file_size ?? row?.size ?? undefined,
                processingStatus: readAttachmentStatus(row) ?? null,
            }));
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    async deleteScopeAttachment(attachmentId: string): Promise<void> {
        try {
            await apiClient.delete(ApiUrls.deleteScopeAttachment(attachmentId));
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Save or update design system for a project.
     * Tries PUT first, falls back to POST on 404 (some backends expect create vs update).
     */
    async saveDesignSystem(projectId: string, payload: any): Promise<any> {
        try {
            const url = `${ApiUrls.getProject(projectId)}/design-system`;
            // Prefer PUT (idempotent update) then fallback to POST if not found
            try {
                const response = await apiClient.put(url, payload);
                return response.data;
            } catch (err: any) {
                if (err?.response?.status === 404) {
                    const response = await apiClient.post(url, payload);
                    return response.data;
                }
                throw err;
            }
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Bulk update multiple projects
     * Used for archiving projects after downgrade to Free plan
     *
     * @param updates - Array of project updates with id and data
     * @returns Response with success message and updated count
     *
     * @example
     * ```typescript
     * await projectService.bulkUpdateProjects([
     *   { id: 'proj-1', data: { isActive: false } },
     *   { id: 'proj-2', data: { isActive: false } },
     * ]);
     * ```
     */
    async bulkUpdateProjects(updates: {projects: BulkUpdateProjectItem[]}): Promise<BulkUpdateProjectsResponse> {
        try {
            const response = await apiClient.patch<BulkUpdateProjectsResponse>(
                ApiUrls.bulkUpdateProjects,
                updates
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Handle API errors consistently
     */
    private handleError(error: any): Error {
        if (error.response) {
            const message = error.response.data?.message || 'Request failed';
            return new Error(message);
        }

        // Network error
        return new Error('Network error. Please check your connection.');
    }
}

export const projectService = new ProjectService();
export default projectService;

